import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useTranslation } from "react-i18next";
import useTranslationFromJson from "../../../../hooks/useTranslationFromJSON";
import clsx from "clsx";

import Dropdown from "./Dropdown";

/** Material-UI */
import { Link } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ArrowDropDown, ArrowDropUp, ArrowRight, ArrowLeft } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
    menuItems: {
        position: "relative",
        // fontSize: "0.88888889vw",
        fontSize: "clamp(14px, 1vw, 18px)",
        color: "rgba(0,0,0,0.87)",
        "& a, & .a": {
            display: "block",
            fontSize: "inherit",
            color: "inherit",
            textDecoration: "none",
            padding: "0.77777778vw 0.66666667vw",
            letterSpacing: "normal",
            fontWeight: 400,
            whiteSpace: "nowrap",
            fontFamily: "'Open Sans', san-serif",
        },
        "& button": {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minWidth: 110,
            color: "inherit",
            fontSize: "inherit",
            border: "none",
            backgroundColor: "transparent",
            cursor: "pointer",
            width: "100%",
            padding: 0,
            "& span:not(.a)": {
                marginLeft: theme.spacing(-3.25),
                marginRight: theme.spacing(1.75),
            },
        },
        "& > a, & button": {
            textAlign: "left",
        },
        "& a:hover, & button:hover": {
            backgroundColor: "#f2f2f2",
            textDecoration: "none",
        },
    },

    active: {
        "&, & > a, & > button > a, & > button > .a": {
            color: theme.palette.primary.main,
            fontWeight: 600,
        },
        "&:after": {
            content: '" "',
            position: "absolute",
            width: "100%",
            height: 2,
            backgroundColor: theme.palette.primary.main,
            bottom: -2,
        },
    },

    activeSub: {
        "&:after": {
            content: '" "',
            position: "absolute",
            width: "100%",
            height: "100%",
            backgroundColor: "transparent", // theme.palette.primary.main,
            borderLeft: `2px solid ${theme.palette.primary.main}`,
            bottom: -0,
        },
    },

    menuChildren: {
        position: "relative",
        fontSize: "0.88888889vw",
        color: "rgba(0,0,0,0.87)",
        "& button": {
            padding: "0.77777778vw 0.66666667vw",
            display: "flex",
            alignItems: "center",
            color: "inherit",
            fontSize: "inherit",
            border: "none",
            backgroundColor: "transparent",
            cursor: "pointer",
            width: "100%",
            "& span": {
                marginLeft: theme.spacing(-2),
            },
        },
    },
    arrow: {
        display: "flex",
        "&::after": {
            // content: "",
            display: "inline-block",
            marginLeft: "0.28em",
            verticalAlign: "0.09em",
            borderTop: "0.42em solid",
            borderRight: "0.32em solid transparent",
            borderLeft: "0.32em solid transparent",
        },
    },
}));

const MenuItems = ({ items, depthLevel, children, onClick, isLast }) => {
    const classes = useStyles();
    const { i18n } = useTranslation();
    let ref = useRef();
    const location = useLocation();
    const { translationFromJson } = useTranslationFromJson();

    const [dropdown, setDropdown] = useState(false);
    const [active, setActive] = useState(false);

    useEffect(() => {
        const _plane = getAllIDs([items]);
        const _hasChildActive = _plane.some((child) => {
            return location.pathname === child.link || location.pathname === child.path;
        });
        const _active = location.pathname === items.link || _hasChildActive;
        setActive(_active);
    }, [location, items]);

    useEffect(() => {
        const handler = (event) => {
            if (dropdown && ref.current && !ref.current.contains(event.target)) {
                setDropdown(false);
            }

            if (event.keyCode === 27) {
                setDropdown(false);
            }
        };
        document.addEventListener("mousedown", handler);
        document.addEventListener("touchstart", handler);
        document.addEventListener("keydown", handler);
        return () => {
            // Cleanup the event listener
            document.removeEventListener("mousedown", handler);
            document.removeEventListener("touchstart", handler);
            document.removeEventListener("keydown", handler);
        };
    }, [dropdown, isLast, depthLevel]);

    const onMouseEnter = () => {
        // window.innerWidth > 960 && setDropdown(true);
    };

    const onMouseLeave = () => {
        window.innerWidth > 960 && setDropdown(false);
    };

    const onButtonClick = (event) => {
        if (event.keyCode === 27) {
            setDropdown(false);
        }
    };

    const handleOnClick = (event) => {
        if (onClick && event.target === event.currentTarget) onClick();
    };

    const handleKeyDown = (event) => {
        if (event.keyCode === 9 && !event.shiftKey && isLast) {
            setDropdown(false);
        }
    };

    return children ? (
        <li className={classes.menuChildren} ref={ref} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <button onClick={handleOnClick}>{children}</button>
        </li>
    ) : (
        <li
            className={clsx(
                classes.menuItems,
                active && classes.active,
                active && depthLevel > 0 && items.children && classes.activeSub
            )}
            aria-current={active ? "page" : false}
            ref={ref}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {items.link && items.children ? (
                <>
                    <button
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={dropdown ? "true" : "false"}
                        onKeyDown={(event) => onButtonClick(event)}
                        onClick={() => setDropdown((prev) => !prev)}
                    >
                        {window.innerWidth < 960 && depthLevel === 0 ? (
                            // t(`mainmenu.${items.name}`)
                            translationFromJson(items, "name")
                        ) : (
                            <Link
                                // href={items.external ? items.link : `#${items.link}`}
                                href={
                                    items.external
                                        ? items.link
                                        // : `${i18n.language === "pl" ? "" : `/${i18n.language}`}${items.link}`
                                        : `/${i18n.language}${items.link}`
                                }
                                tabIndex={-1}
                                tabindex={-1}
                                target={items.target}
                            >
                                {/* {t(`mainmenu.${items.name}`)} */}
                                {translationFromJson(items, "name")}
                            </Link>
                        )}

                        {depthLevel > 0 && window.innerWidth < 960 ? null : depthLevel > 0 &&
                          window.innerWidth > 960 ? (
                            <span>{dropdown ? <ArrowLeft /> : <ArrowRight />}</span>
                        ) : (
                            <span className={classes.arrow}>{dropdown ? <ArrowDropUp /> : <ArrowDropDown />}</span>
                        )}
                    </button>
                    <Dropdown depthLevel={depthLevel} submenus={items.children} dropdown={dropdown} isLast={isLast} />
                </>
            ) : !items.link && items.children ? (
                <>
                    <button
                        type="button"
                        aria-haspopup="listbox"
                        aria-expanded={dropdown ? "true" : "false"}
                        onClick={() => setDropdown((prev) => !prev)}
                        onKeyDown={(event) => onButtonClick(event)}
                    >
                        <span className="a">
                            {/* {t(`mainmenu.${items.name}`)}  */}
                            {translationFromJson(items, "name")}
                        </span>
                        {depthLevel > 0 ? (
                            <span>
                                {/* <ArrowRight /> */}
                                {dropdown ? <ArrowLeft /> : <ArrowRight />}
                            </span>
                        ) : (
                            <span className={classes.arrow}>
                                {/* <ArrowDropDown /> */}
                                {dropdown ? <ArrowDropUp /> : <ArrowDropDown />}
                            </span>
                        )}
                    </button>
                    <Dropdown depthLevel={depthLevel} submenus={items.children} dropdown={dropdown} isLast={isLast} />
                </>
            ) : (
                <Link
                    // href={items.external ? items.link : `#${items.link}`}
                    href={
                        items.external
                            ? items.link
                            // : `${i18n.language === "pl" ? "" : `/${i18n.language}`}${items.link}`
                            : `/${i18n.language}${items.link}`
                    }
                    target={items.target}
                    aria-label={items.ariaLabel}
                    onKeyDown={handleKeyDown}
                >
                    {/* {t(`mainmenu.${items.name}`)} */}
                    {translationFromJson(items, "name")}
                </Link>
            )}
        </li>
    );
};

export default MenuItems;

const getAllIDs = (data) => {
    let children = [];

    return data
        .map((m) => {
            if (m.children && m.children.length) {
                children = [...children, ...m.children];
            }
            return m;
        })
        .concat(children.length ? getAllIDs(children) : children);
};
